<template>
  <div class="relative" @mouseleave="onClose" @mouseenter="onEnter">
    <span v-if="name" :id="`label-${uuid}`" class="sr-only">
      {{ name }}
    </span>
    <div v-if="searchable" style="position: relative">
      <input
        ref="search"
        aria-haspopup="listbox"
        type="search"
        class="flex w-full items-center justify-between border border-grey-30 bg-white px-4 py-2 text-left text-sm text-black"
        :name="name"
        :value="searchRender(searchVal)"
        :class="buttonClassnames"
        :aria-labelledby="labelledByAria"
        :placeholder="placeholder"
        @focus="onSearching"
        @input="onSearch"
        @blur="onStopSearch"
        @keypress.enter="onStopSearch"
        @click.left="toggleDropdown"
      />
      <chevron-down class="absolute right-4 top-2 h-5 w-5" />
    </div>
    <button
      v-if="!searchable"
      class="flex w-full items-center justify-between border border-grey-25 bg-white px-4 py-2 text-left text-sm text-grey-70 rounded"
      :class="buttonClassnames"
      :id="`btn-${uuid}`"
      :aria-labelledby="labelledByAria"
      :disabled="disabled"
      aria-haspopup="listbox"
      @mousedown="toggleDropdown"
      @click.prevent
      @keyup.enter.space="setCollapseOpen(true)"
    >
      <span v-if="showPlaceholder">
        {{ placeholder }}
      </span>
      <slot v-if="!showPlaceholder" :val="val" name="selected">
        {{ val }}
      </slot>
      <chevron-down class="h-5 w-5" />
    </button>
    <input-error-popup
      :error-message="errorMessage"
      v-if="this.showErrorPopup"
    />
    <ul
      ref="collapse"
      class="absolute z-50 max-h-64 w-full overflow-y-scroll border border-t-0 border-grey-30 bg-white"
      role="listbox"
      :class="itemsClassnames"
      :aria-activedescendant="
        this.val !== null ? `ah-dd-${uuid}-${selectedKey}` : false
      "
      tabindex="-1"
      @click.right="onClose"
    >
      <li
        v-if="!hasFiltered"
        class="bg-grey-10"
        disabled
        tabindex="-1"
        @click.prevent
      >
        <div class="dropdown-item">
          {{ noItemsLabel }}
        </div>
      </li>
      <li
        v-else
        v-for="(item, key, idx) in filtered"
        ref="dropdownItems"
        :key="keyProvider(item, key, idx)"
        :id="optionId(key, idx)"
        :aria-selected="val === item"
        role="option"
        tabindex="0"
        class="cursor-pointer px-4 py-2 text-sm hover:bg-grey-20"
        @keyup.enter="onSelect(item, key, $event)"
        @click.left.prevent="onSelect(item, key, $event)"
        @blur="onBlur"
      >
        <div class="dropdown-item">
          <slot :item="item" name="item">
            {{ item }}
          </slot>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import InputErrorPopup from 'Components/InputErrorPopup';
import showErrorPopup from 'Mixins/showErrorPopup';
import input from 'Mixins/input';
import keyProvider from 'Mixins/keyProvider';
import uuid from 'Mixins/uuid';
import ChevronDown from 'Components/Icons/IconChevronDown';

export default {
  name: 'InputDropdown',
  components: {
    ChevronDown,
    InputErrorPopup,
  },
  mixins: [uuid, input, keyProvider, showErrorPopup],
  props: {
    searchable: {
      type: Boolean,
      default: false,
    },
    searcher: {
      type: Function,
      default: (items, search) => {
        if (search === null) {
          return items;
        }

        const isArray = Array.isArray(items);

        if (isArray) {
          return items.filter((item) => {
            if (typeof item === 'string') {
              return item.includes(search);
            } else {
              return item === search;
            }
          });
        }

        return Object.keys(items).reduce((carry, key) => {
          const item = items[key];

          if (typeof item === 'string' && item.includes(search)) {
            carry[key] = item;
          } else if (item === search) {
            carry[key] = item;
          }

          return carry;
        }, {});
      },
    },
    searchRender: {
      type: Function,
      default: (value) => value,
    },
    type: {
      type: String,
      default: 'white',
    },
    size: {
      type: String,
      default: 'small',
    },
    items: {
      type: [Array, Object],
      required: true,
    },
    initial: {
      type: [String, Number, Object, Date, Array, Function],
      default: null,
    },
    open: {
      type: Boolean,
      default: null,
    },
    value: {
      type: [String, Number, Object, Date, Array, Function],
      default: null,
    },
    noItemsLabel: {
      type: String,
      default: 'No items match your search.',
    },
  },
  data() {
    let internalValue = null;

    if (Array.isArray(this.items)) {
      const exists = this.items.some((item) => item === this.initial);

      if (exists) {
        internalValue = this.initial;
      }
    } else if (this.items[this.initial]) {
      internalValue = this.items[this.initial];
    }

    return {
      search: null,
      searching: false,
      timeoutId: null,
      internalValue,
      internalOpen: false,
      internalImmediateClose: true,
    };
  },
  computed: {
    dropdownItems() {
      return this.$refs.dropdownItems || [];
    },
    buttonClassnames() {
      return {
        'bg-red-200': this.error,
      };
    },
    itemsClassnames() {
      return {
        invisible: !this.isOpen,
      };
    },
    isArray() {
      return Array.isArray(this.items);
    },
    isOpen() {
      return this.open !== null ? this.open : this.internalOpen;
    },
    showPlaceholder() {
      return !this.value && !(this.value === null && this.internalValue);
    },
    val() {
      if (this.value) {
        return this.isArray ? this.value : this.items[this.value];
      }

      return this.internalValue;
    },
    searchVal() {
      return this.searching ? this.search : this.val;
    },
    labelledByAria() {
      if (this.name === null) {
        // Returning false here causes the attribute
        // to be omitted from markup.
        return false;
      }

      return `label-${this.uuid}`;
    },
    selectedKey() {
      const val = this.value === null ? this.internalValue : this.value;

      return this.isArray
        ? this.items.indexOf(val)
        : Object.keys(this.items).find((key) => this.items[key] === val);
    },
    filtered() {
      if (!this.searchable || this.search === null) {
        return this.items;
      }

      return this.searcher(this.items, this.search);
    },
    hasFiltered() {
      return (this.isArray ? this.filtered : Object.keys(this.filtered)).length;
    },
  },
  methods: {
    optionId(key, idx) {
      return `ah-dd-${this.uuid}-${this.getIndex(key, idx)}`;
    },
    onClose(e) {
      if (!this.isOpen) {
        return;
      }

      if (this.dropdownItems.includes(e.relatedTarget)) {
        return;
      }

      this.timeoutId = setTimeout(() => {
        this.$refs.search && this.$refs.search.blur();
        this.setCollapseOpen(false);
      }, 150);
    },
    onEnter() {
      clearTimeout(this.timeoutId);
    },
    onSelect(value, key, event) {
      this.internalValue = value;

      this.$emit('change', this.isArray ? value : key, event);

      this.$nextTick(() => {
        this.$refs.search && this.$refs.search.blur();
        this.setCollapseOpen(false);
        this.search = null;
        this.searching = false;
      });
    },
    onSearching(e) {
      this.$emit('beginSearch', e.target.value);

      this.searching = true;
    },
    onSearch(e) {
      const val = e.target.value;

      this.setCollapseOpen(true);

      if (this.search === null && val === ' ') {
        return;
      }

      this.$emit('searchInput', val);
      this.search = val;
    },
    onBlur(e) {
      const local = [...this.dropdownItems, this.$refs.search];

      if (!local.includes(e.relatedTarget)) {
        this.setCollapseOpen(false);
        this.search = null;
        this.searching = false;

        return true;
      }

      return false;
    },
    onStopSearch(e) {
      if (e.type === 'blur' && this.onBlur(e)) {
        return;
      }

      if (!this.searching || this.search === null) {
        this.search = null;
        this.searching = false;

        return;
      }

      this.$emit('searched', e.target.value);

      let val = e.target.value;

      if (this.hasFiltered === 1) {
        val = this.isArray ? this.filtered[0] : Object.values(this.filtered)[0];
      }

      let key = null;

      if (this.isArray && this.items.includes(val)) {
        key = this.items.indexOf(val);
      }

      if (Object.values(this.items).includes(val)) {
        key = Object.keys(this.items).find((key) => this.items[key] === val);
      }

      if (key === null) {
        return;
      }

      this.onSelect(val, key);
    },
    toggleDropdown() {
      this.setCollapseOpen(!this.isOpen);
    },
    setCollapseOpen(state) {
      this.$emit('collapse', state);
      this.internalOpen = state;
    },
    getIndex(key, idx) {
      return key === undefined ? idx : key;
    },
  },
};
</script>
